/* SS06256: the preview's two devices are separate apps (the web one shares this origin, the app one
   does not), each holding its own current workspace in memory. A device that switches workspace
   says so with { type: 'inistate', action: 'workspaceChanged', device, wsId }; the preview then
   re-points the other device at that workspace the next time it is shown. */

import { DOCUMENT_ID_PARAM } from "./previewDocument";

export const WORKSPACE_CHANGED = "workspaceChanged";

export const DeviceKey = Object.freeze({
  WEB: "web",
  APP: "app",
});

export const sameWorkspace = (a, b) =>
  a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

/** The { device, wsId } a device message announces, or null when it isn't a workspace change. */
export const readWorkspaceChange = (message) => {
  if (!message || message.action !== WORKSPACE_CHANGED) return null;
  if (message.device !== DeviceKey.WEB && message.device !== DeviceKey.APP) return null;
  if (message.wsId === null || message.wsId === undefined || message.wsId === "") return null;
  return { device: message.device, wsId: message.wsId };
};

/* SS06257: a route one device reports is for the OTHER device. Picking the target by which device
   is visible sent a hidden device's report back to itself. A message that names no sender keeps the
   old rule: the device that isn't shown. */
export const forwardTargetOf = (sender, visibleDeviceKey) => {
  if (sender === DeviceKey.WEB) return DeviceKey.APP;
  if (sender === DeviceKey.APP) return DeviceKey.WEB;
  return visibleDeviceKey === DeviceKey.WEB ? DeviceKey.APP : DeviceKey.WEB;
};

/* SS06260: identifies the entry (and activity) a route shows, to recognise a device echoing back the
   entry it was just sent - e.g. Desktop adding its default listing (?l=..) and reporting the route
   again. Not the page: an entry over Mobile's Home is the same entry over Desktop's dashboard.
   null when the route shows no entry. SS06258: the PDF document open over it too - opening one on
   the entry a device was just sent is news, not an echo. */
export const entryKeyOf = (route) =>
  route && route.query && route.query.id
    ? `${route.query.id}|${route.query.a || ""}|${route.query[DOCUMENT_ID_PARAM] || ""}`
    : null;

/* SS06260: the entry query of an app root link (#/?id=..&a=..) - a form open over Mobile's Home -
   or null for any other app route. */
export const appHomeEntryQuery = (url) => {
  const hash = typeof url === "string" && url.includes("#") ? url.substring(url.indexOf("#") + 1) : null;
  if (hash === null) return null;
  const [path, search] = hash.split("?");
  if (path !== "/" && path !== "") return null;
  const query = Object.fromEntries(new URLSearchParams(search || ""));
  return query.id ? query : null;
};

/* The entry, activity and open PDF document (SS06258) of a form moved between Mobile's Home and
   Desktop's dashboard. */
const entryParams = (query) => {
  const params = new URLSearchParams({ id: query.id });
  if (query.a) params.set("a", query.a);
  if (query[DOCUMENT_ID_PARAM]) params.set(DOCUMENT_ID_PARAM, query[DOCUMENT_ID_PARAM]);
  return params;
};

/* SS06260: a form open over Mobile's Home is shown over Desktop's dashboard. */
export const webUrlForAppHome = (baseLocationOrigin, query) =>
  `${baseLocationOrigin}/#/dashboard?${entryParams(query).toString()}`;

/* SS06257: the app has no dashboard form view, so a dashboard route reaches it as its home. With an
   entry open on the dashboard (?id=..&a=..) the app gets a root link carrying only that entry and
   activity, which it opens over its home (the app opens a preview root link over its existing Home,
   without echoing it); a plain dashboard stays the bare origin. */
export const appUrlForDashboard = (baseLocationOrigin, query = {}) => {
  if (!query.id) return baseLocationOrigin;
  return `${baseLocationOrigin}/#/?${entryParams(query).toString()}`;
};

/** Start-up URLs for both devices; each app signs in and opens `wsId` from this query. */
export const buildDeviceUrls = ({ webBaseUrl, appBaseUrl, accessToken, refreshToken, wsId, origin }) => {
  const query = `accessToken=${accessToken}&refreshToken=${refreshToken}&wsId=${wsId}&origin=${origin}`;
  return {
    [DeviceKey.WEB]: `${webBaseUrl}/#/dashboard?${query}`,
    [DeviceKey.APP]: `${appBaseUrl}/?${query}`,
  };
};
